import { useCallback, useEffect, useRef } from "react";
import { UseDisclosureReturn } from "@chakra-ui/react";
import { useGenericActivityFromElysiumMutation } from "src/api/client/generic-activity-from-elysium.api";
import { EnumActivityType } from "src/api/client/generic-activity.api";
import { runAfterLoadWhenIdle } from "src/utils/dom/idle/idle";

type TChatState = Pick<UseDisclosureReturn, "isOpen" | "onClose" | "onToggle">;
interface IMessageClicked {
    id: string;
}

const HELPSCOUT_BEACON_ID = "66499048-f129-4781-a9c6-254b100003ac";

interface IChatHandlers {
    onClose: () => void;
    onMessageClicked: (message: IMessageClicked) => void;
}

let chatInitialization: Promise<void> | undefined;

async function loadHelpScoutScript() {
    return new Promise<void>(resolve => {
        // needed procedure before we load the helpscout script
        window.Beacon = (() => {
            const _Beacon: typeof window.Beacon = function (t, n, a) {
                window.Beacon.readyQueue.push({ method: t, options: n, data: a });
            };
            _Beacon.readyQueue = [];
            return _Beacon;
        })();

        const script = document.createElement("script");
        script.type = "text/javascript";
        script.src = "https://beacon-v2.helpscout.net";
        script.onload = () => {
            resolve();
        };

        document.head.appendChild(script);
    });
}

function setChatGradientVisibility(isVisible: boolean) {
    const display = isVisible ? "display: block" : "display: none";
    document.querySelector(".Beacon [class*='Gradient']")?.setAttribute("style", display);
}

async function initChat({ onClose, onMessageClicked }: IChatHandlers) {
    await loadHelpScoutScript();

    window.Beacon("init", HELPSCOUT_BEACON_ID);
    window.Beacon("on", "close", onClose);
    window.Beacon("on", "message-clicked", onMessageClicked);
    window.Beacon("on", "ready", () => {
        setChatGradientVisibility(false);
    });
}

const initChatOnce = (handlers: IChatHandlers) => {
    chatInitialization ??= initChat(handlers);

    return chatInitialization;
};

function openChat() {
    if (window.Beacon) {
        window.Beacon("open");
        setChatGradientVisibility(true);
    }
}

function closeChat() {
    if (window.Beacon) {
        window.Beacon("close");
        setChatGradientVisibility(false);
    }
}

export const useHelpscoutChat = (state: TChatState, showServiceButtons: boolean) => {
    const { onClose, onToggle, isOpen } = state;
    const genericActivity = useGenericActivityFromElysiumMutation();
    const handlersRef = useRef<IChatHandlers>();

    useEffect(() => {
        handlersRef.current = {
            onClose,
            onMessageClicked: ({ id }) => {
                onToggle();
                genericActivity({
                    activityTypeId: EnumActivityType.HelpscoutMessageClicked,
                    parameter: `messageId=${id}`,
                    referrer: window.location.href || null,
                });
            },
        };
    });

    const startChat = useCallback(
        () =>
            initChatOnce({
                onClose: () => handlersRef.current?.onClose(),
                onMessageClicked: message => handlersRef.current?.onMessageClicked(message),
            }),
        []
    );

    useEffect(() => {
        if (!showServiceButtons) {
            return undefined;
        }

        return runAfterLoadWhenIdle(() => {
            startChat();
        });
    }, [showServiceButtons, startChat]);

    useEffect(() => {
        if (!isOpen) {
            closeChat();
            return undefined;
        }

        let isCancelled = false;

        startChat().then(() => {
            if (!isCancelled) {
                openChat();
            }
        });

        return () => {
            isCancelled = true;
            closeChat();
        };
    }, [isOpen, startChat]);
};
